import moment from "moment";
import "moment-timezone";
import { customFormat, DayOfWeekDayShort, DayOfWeekMonthDayShort, DayOfWeekMonthDayYear, DayOfWeekMonthDayYearShort } from "./momentutils.customformats";

export type DateTimeLikeObject = moment.Moment | Date | string;

export const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Returns an incoming moment unchanged so its timezone info is preserved; wraps non-moments via moment.utc.
export const getMoment = (m: DateTimeLikeObject): moment.Moment => {
  return moment.isMoment(m) ? m : moment.utc(m);
};

export const is24Hour = () => {
  return !moment.localeData().longDateFormat("LT").includes("h");
}

export const firstDayOfWeek = () => {
  return moment.localeData().firstDayOfWeek();
}

export const timeSeparator = () => ":";

export const formatTime = (m: DateTimeLikeObject): string => {
  const timeFormat = is24Hour() ? "HH:mm" : "hh:mm A";
  return getMoment(m).format(timeFormat);
};

export const formatTimeRange = (start: string | undefined, end: string | undefined): string =>
  [start, end].filter(Boolean).join(" - ");

// Example format: "Fri, 1"
export const formatWrittenDay = (m: moment.Moment) => {
  return customFormat(m, DayOfWeekDayShort);
}

// Example format: Fri, 1 Mar
export const formatWrittenDayMonth = (m: moment.Moment) => {
  return customFormat(m, DayOfWeekMonthDayShort);
};

// Example: "Tue"
export const formatWeekDay = (m: moment.Moment) => {
  return m.format("ddd");
};

// Example format in US: "Friday, 2 May 2025 01:00"
export const formatLongDateWithTime = (m: DateTimeLikeObject) => {
  return getMoment(m).format("LLLL");
}

export const formatShortDateWithTime = (m: moment.Moment) => {
  return m.format("llll");
}

// Example fromat in US: "Wed, Sep 18, 2024 4:30 PM EEST"
export const formatShortDateWithTimeZone = (m: moment.Moment) => {
  return m.tz(systemTimezone).format("llll zz");
}

// Example format in US: "Tue, February 16, 2021"
export const formatWrittenDayMonthYear = (d: DateTimeLikeObject) => {
  return customFormat(getMoment(d), DayOfWeekMonthDayYear);
};

// Example format in US: "Fri, Mar 21, 2025"
export const formatWrittenDayMonthYearShort = (d: DateTimeLikeObject) => {
  return customFormat(getMoment(d), DayOfWeekMonthDayYearShort);
};

// Example format in US: "7/2/2012"
export const formatShortDate = (m: DateTimeLikeObject) => {
  return getMoment(m).format("L");
};

// Example format in US: "March 7, 2024"
export const formatLongDate = (m: DateTimeLikeObject) => {
  return getMoment(m).format("LL");
}

// Example format in US: "7/2/2012 (13 years)"
export const formatBirthdateWithAge = (birthdate: DateTimeLikeObject, yearsOldLabel: string) => {
  const resolved = getMoment(birthdate);
  const age = moment.utc().diff(resolved, "years");
  return `${formatShortDate(resolved)} (${age} ${yearsOldLabel})`;
};

// Example format in US: "7/2/2012 11:05PM"
export const formatShortDateAndTime = (m: DateTimeLikeObject) => {
  const resolved = getMoment(m);
  const date = formatShortDate(resolved);
  const time = formatTime(resolved);
  return `${date} ${time}`;
};

export const formatDayWithDate = (m: moment.Moment) => {
  return `${formatWeekDay(m)} ${formatShortDate(m)}`;
};

export const formatWrittenDateTimeUtc = (m: moment.Moment) => {
  return `${formatShortDateAndTime(m)} UTC`;
};

export const getNumberOfNights = (start: moment.Moment, end: moment.Moment): number => {
  return end.diff(start, "days");
}

export const parseFromTime = (time: server.dto.Time): moment.Moment => {
  return parseFromTimeString(time.cultureInvariant);
};

export const parseFromTimeString = (time: string): moment.Moment => {
  return moment.utc(time, "HH:mm");
};

// The localized words and templates needed to phrase a duration. The fields
// mirror the translation keys so each platform can fill them from its own
// resource system.
export interface TimeDifferenceStrings {
  YYear: string;
  XYears: string;
  YMonth: string;
  XMonths: string;
  YDay: string;
  days: string;
  YHour: string;
  XHours: string;
  YMinute: string;
  XMinutes: string;
  YSecond: string;
  XSeconds: string;
  night: string;
  nights: string;
}

// Phrases the gap between two moments, like "2 years, 5 days". The wording comes
// in through strings so this stays free of any resource system.
export const formatTimeDifference = (start: moment.Moment, end: moment.Moment, strings: TimeDifferenceStrings): string => {
  const units: Array<[moment.unitOfTime.Diff, string, (diff: number) => string]> = [
    ["years", strings.YYear, diff => strings.XYears.replace("{0}", diff.toString())],
    ["months", strings.YMonth, diff => strings.XMonths.replace("{0}", diff.toString())],
    ["days", strings.YDay, diff => `${diff} ${strings.days}`], // Manual construction for "days"
    ["hours", strings.YHour, diff => strings.XHours.replace("{0}", diff.toString())],
    ["minutes", strings.YMinute, diff => strings.XMinutes.replace("{0}", diff.toString())],
    ["seconds", strings.YSecond, diff => strings.XSeconds.replace("{0}", diff.toString())]
  ];
  start = start.clone();
  end = end.clone();

  const parts: string[] = [];
  units.forEach(([unit, singular, formatPlural]) => {

    // Exception for months, as we don't want to show months if the difference is less than 100 days
    // So 90 days should be shown as: "90 days"
    // But 110 days should be shown as: "3 months, 20 days"
    if (unit === "months") {
      const differenceInDays = end.diff(start, "days");
      if (differenceInDays > 27 && differenceInDays < 100) {
        const daysUnit = units.find(u => u[0] === "days");
        if (daysUnit) {
          const [, , daysUnitPlural] = daysUnit;
          parts.push(daysUnitPlural(differenceInDays));
          start = start.add(differenceInDays, "days");
          return;
        }
      }
    }

    const diff = end.diff(start, unit);
    if (diff > 0) {
      start = start.add(diff, unit); // Move the start time forward
      if (diff === 1) {
        parts.push(singular);
      } else {
        parts.push(formatPlural(diff));
      }
    }
  });

  return parts.length > 0 ? parts.join(", ") : strings.XMinutes.replace("{0}", "0");
};

// Phrases a number of nights, like "1 night" or "3 nights".
export const formatNights = (start: moment.Moment, end: moment.Moment, strings: TimeDifferenceStrings): string => {
  const diff = getNumberOfNights(start, end);

  if (diff === 1) {
    return `${diff} ${strings.night}`;
  }

  return `${diff} ${strings.nights}`;
};
